import { useState } from "react";
import { Table, Button, message, DatePicker, Tag } from "antd";
import dayjs from "dayjs";
import { PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useReports from "./useReports";

const { RangePicker } = DatePicker;

function Reports() {
  const navigate = useNavigate();
  const {
    country,
    section,
    isOcean,
    isAdmin,
    pageTitle,
    data,
    isLoading,
    isFetching,
    downloading,
    handleGenerate,
    tableColumns,
  } = useReports();


  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null);

  const onGenerate = async () => {
    // Prevent double-click while a generation is already in progress
    if (downloading?.generating) {
      message.info({ content: "Ya hay una generación en curso. Por favor espera a que termine.", key: "generate" });
      return;
    }

    let start, end;
    if (!isOcean && dateRange && dateRange[0] && dateRange[1]) {
      start = dateRange[0].format("YYYY-MM-DD");
      end = dateRange[1].format("YYYY-MM-DD");
    }

    if (isOcean) {
      message.loading({
        content: "Generación iniciada. El boletín aparecerá en la lista cuando esté listo (puede tomar 5-30 min según el tipo).",
        key: "generate",
        duration: 8,
      });
    } else {
      message.loading({ content: "Generando boletín...", key: "generate", duration: 0 });
    }

    try {
      await handleGenerate(start, end);
      if (!isOcean) {
        message.success({ content: "Boletín generado con éxito", key: "generate" });
      }
    } catch {
      message.error({ content: "Error al iniciar la generación del boletín", key: "generate" });
    }
  };


  return (
    <div>
      {/* ── Breadcrumb de retroceso ── */}
      <div style={{ marginBottom: 12 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/admin/reports")}
          style={{
            padding: 0,
            color: "#8c8c8c",
            fontWeight: 500,
            fontSize: 13,
          }}
        >
          Boletines Oceanográficos
        </Button>
        <span style={{ color: "#8c8c8c", margin: "0 6px" }}>›</span>
        <span style={{ color: "#1a1a2e", fontWeight: 600, fontSize: 13 }}>
          {pageTitle}
        </span>
      </div>

      {/* ── Header con título y acciones ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ margin: 0 }}>
            Boletines Oceanográficos - {pageTitle}
          </h2>
          {isOcean ? (
            <Tag color="blue">🌊 Copernicus</Tag>
          ) : (
            <Tag color="green">📊 Acústico</Tag>
          )}
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          {/* Solo el administrador puede generar nuevos boletines */}
          {isAdmin && (
            <>
              {/* Date picker only for acoustic bulletins */}
              {!isOcean && (
                <RangePicker
                  value={dateRange as any}
                  onChange={(dates) => setDateRange(dates as any)}
                  allowClear
                  format="YYYY-MM-DD"
                  placeholder={["Inicio de temp.", "Fin de temp."]}
                />
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onGenerate}
                loading={downloading?.generating}
                disabled={downloading?.generating}
                title={downloading?.generating ? "Ya hay una generación en curso..." : "Generar nuevo boletín"}
                style={{
                  borderRadius: "8px",
                  background: downloading?.generating ? "#d9d9d9" : "#81BC3D",
                  borderColor: downloading?.generating ? "#d9d9d9" : "#81BC3D",
                }}
              >
                {downloading?.generating ? "En progreso..." : "Generar Boletín"}
              </Button>
            </>
          )}
        </div>
      </div>

      <Table
        dataSource={data}
        columns={tableColumns}
        rowKey={(record) => record.name || record.key}
        loading={isLoading}
        locale={{
          emptyText: isLoading
            ? "Cargando..."
            : "No hay boletines generados aún para esta sección.",
        }}
        pagination={{
          pageSizeOptions: ["5", "10", "20", "50"],
          showSizeChanger: true,
          position: ["bottomCenter"],
        }}
      />
    </div>
  );
}

export default Reports;
